import { useEffect, useMemo, useState } from 'react'
import { Badge, HStack, Stack } from '@chakra-ui/react'

import { Dropzone } from 'components'
import CustomFormFileCardToUploadFileOnMultipleUpload from 'components/tools/CustomForm/commonComponents/CustomFormFileCardToUploadFileOnMultipleUpload'
import CustomFormFileCardToGetFile from 'components/tools/CustomForm/commonComponents/CustomFormFileCardToGetFile'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import { UploadFile } from 'types'
import { convertBytesNumberToString } from 'utils/transforms/number'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import validateMultipleFileUploadOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateMultipleFileUploadOnCustomForm'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormMultipleUploadQuestion({
  question,
  questionKey,
  formMethods,
  disabled,
}: CustomFormViewerQuestionProps) {
  const [files, setFiles] = useState<UploadFile[]>([])

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  async function onUpload(files: UploadFile | UploadFile[]) {
    if (!Array.isArray(files)) return
    if (files.length === 0) return
    const errorMessage = validateMultipleFileUploadOnCustomForm({ files, question })

    if (errorMessage === '') {
      setFiles((oldFiles) => {
        return [...oldFiles, ...files]
      })
      removeErrorFromQuestion(question.slug)
    } else {
      setErrorOnQuestion(question, errorMessage)
    }
  }

  const filesValues = useMemo(() => {
    return question?.answer ? question?.answer : []
  }, [])

  useEffect(() => {
    if (formMethods?.setValue) {
      if (question?.answer) {
        formMethods?.setValue(questionKey, question?.answer)
      } else {
        formMethods?.setValue(questionKey, [])
      }
    }
  }, [question?.answer])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  return (
    <Stack id={question.slug}>
      <Dropzone isMultiple isDisabled={disabled} onUpload={onUpload} />

      {files.length > 0 && (
        <Stack>
          {files.map((fileItem) => {
            return (
              <CustomFormFileCardToUploadFileOnMultipleUpload
                key={fileItem.name}
                questionKey={questionKey}
                currentFile={fileItem}
              />
            )
          })}
        </Stack>
      )}

      {filesValues && filesValues.length > 0 && files.length > 0 && (
        <Stack>
          {filesValues.map((file) => {
            const [fileId, fileSlug, archiveId] = file
            return (
              <CustomFormFileCardToGetFile
                key={fileId}
                fileId={fileId as string}
                fileSlug={fileSlug as string}
                archiveId={archiveId as string}
              />
            )
          })}
        </Stack>
      )}

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />

      {question.validation && (
        <HStack flexWrap="wrap">
          {question.validation.min && (
            <Badge textTransform="none">Tamanho mínimo ({convertBytesNumberToString(question.validation.min)})</Badge>
          )}
          {question.validation.max && (
            <Badge textTransform="none">Tamanho máximo ({convertBytesNumberToString(question.validation.max)})</Badge>
          )}
          {question.validation.min_file && (
            <Badge textTransform="none">Mínimo de arquivos permitidos ({question.validation.min_file})</Badge>
          )}
          {question.validation.max_file && (
            <Badge textTransform="none">Máximo de arquivos permitidos ({question.validation.max_file})</Badge>
          )}
          {question.validation.extensions && (
            <Badge textTransform="none">Extensões permitidas ({question.validation.extensions?.join(', ')})</Badge>
          )}
        </HStack>
      )}
    </Stack>
  )
}
